import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { getContentById, getContentByIds } from '../data/mockData';
import { addToWatchlist, removeFromWatchlist } from '../redux/slices/userSlice';
import { setCurrentlyPlaying } from '../redux/slices/contentSlice';
import ContentRow from './ContentRow';
import apiService from '../services/apiService';

const DetailsContainer = styled.div`
  min-height: 100vh;
  background-color: #141414;
`;

const HeroSection = styled.div`
  position: relative;
  height: 70vh;
  min-height: 500px;
  display: flex;
  align-items: flex-end;
  padding: 0 4% 4rem;
  background: linear-gradient(
    77deg,
    rgba(0, 0, 0, 0.8) 0%,
    transparent 85%
  ),
  linear-gradient(
    to bottom,
    transparent 0%,
    rgba(20, 20, 20, 1) 100%
  ),
  url(${props => props.$backgroundImage}) center/cover no-repeat;

  @media (max-width: 768px) {
    height: 60vh;
    min-height: 400px;
    padding: 0 20px 2rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  color: white;
  font-size: 1.5rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const ContentWrapper = styled(motion.div)`
  max-width: 600px;
  z-index: 2;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Metadata = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  color: #d2d2d2;

  @media (max-width: 768px) {
    gap: 0.5rem;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

const Rating = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
`;

const Separator = styled.span`
  color: #808080;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.5;
  margin-bottom: 2rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.8rem;
  }
`;

const PlayButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  color: black;
  border: none;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const MyListButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(109, 109, 110, 0.7);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(109, 109, 110, 0.4);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const DetailsSection = styled.section`
  padding: 2rem 4% 4rem;

  @media (max-width: 768px) {
    padding: 1.5rem 20px 3rem;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-bottom: 2rem;
  }
`;

const MainDetails = styled.div``;

const Sidebar = styled.div``;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: white;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const CastList = styled.div`
  color: #808080;
  line-height: 1.6;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const GenreList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const GenreTag = styled.span`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const TrailerSection = styled.div`
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const TrailerFrame = styled.iframe`
  width: 100%;
  height: 315px;
  border: none;
  border-radius: 8px;

  @media (max-width: 768px) {
    height: 240px;
  }
`;

const SourcesSection = styled.div`
  margin-bottom: 2rem;
`;

const SourcesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 0.8rem;
`;

const SourceButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => {
    switch(props.$source) {
      case 'WatchSeries': return 'rgba(229, 9, 20, 0.8)';
      case 'GoodMoviesHD': return 'rgba(46, 211, 105, 0.8)';
      case '0123Movie': return 'rgba(59, 130, 246, 0.8)';
      case 'MegaShareMovie': return 'rgba(168, 85, 247, 0.8)';
      case 'PlutoTV': return 'rgba(251, 146, 60, 0.8)';
      case 'TubiTV': return 'rgba(236, 72, 153, 0.8)';
      case 'Plex': return 'rgba(234, 179, 8, 0.8)';
      case 'RokuChannel': return 'rgba(20, 184, 166, 0.8)';
      case 'XumoPlay': return 'rgba(99, 102, 241, 0.8)';
      default: return 'rgba(109, 109, 110, 0.7)';
    }
  }};
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
`;

const SourceBadgeText = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
`;

const ContentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentProfile } = useSelector(state => state.user);
  const { categories, scrapedContent, apiHealthy } = useSelector(state => state.content);

  const [content, setContent] = useState(null);
  const [sources, setSources] = useState([]);

  useEffect(() => {
    const fetchMovieData = async () => {
      let contentData = null;

      // First try to fetch from Firebase database by movie ID
      if (apiHealthy) {
        try {
          console.log(`Fetching movie with ID: ${id}`);
          contentData = await apiService.getMovieById(id);
          console.log('Firebase movie data:', contentData);
        } catch (error) {
          console.log('Error fetching from Firebase:', error);
        }
      }

      // Fallback to scraped content if Firebase lookup fails
      if (!contentData && apiHealthy) {
        const numericId = parseInt(id);
        
        // Search in scraped movies
        if (scrapedContent.movies && scrapedContent.movies.length > 0) {
          contentData = scrapedContent.movies.find(item => item.id === numericId);
        }

        // Search in scraped trending if not found in movies
        if (!contentData && scrapedContent.trending && scrapedContent.trending.length > 0) {
          contentData = scrapedContent.trending.find(item => item.id === numericId);
        }

        // Search in scraped TV shows if not found yet
        if (!contentData && scrapedContent.tvShows && scrapedContent.tvShows.length > 0) {
          contentData = scrapedContent.tvShows.find(item => item.id === numericId);
        }
      }

      // Final fallback to mock data
      if (!contentData) {
        const numericId = parseInt(id);
        contentData = getContentById(numericId);
      }
      
      setContent(contentData);

      // Fetch all available sources for this content
      if (apiHealthy && contentData) {
        try {
          // Use the original string ID for Firebase movies, numeric ID for scraped content
          const sourceId = typeof contentData.id === 'string' ? contentData.id : id;
          const sourcesData = await apiService.getContentSources(sourceId);
          setSources(sourcesData || []);
        } catch (error) {
          console.log('Error fetching content sources:', error);
          setSources([]);
        }
      }
    };

    fetchMovieData();
  }, [id, scrapedContent, apiHealthy]);

  const handleClose = () => {
    navigate(-1);
  };

  const handlePlay = () => {
    if (content) {
      // Redirect to external watch page for scraped content, fallback to local player
      if (content.watchseriesUrl) {
        window.open(content.watchseriesUrl, '_blank');
      } else {
        dispatch(setCurrentlyPlaying(content));
      }
    }
  };

  const handleMyListToggle = () => {
    if (!content || !currentProfile) return;

    const isInWatchlist = currentProfile.watchlist.includes(content.id);

    if (isInWatchlist) {
      dispatch(removeFromWatchlist(content.id));
    } else {
      dispatch(addToWatchlist(content.id));
    }
  };

  if (!content) {
    console.log('[ContentDetails] No content data available');
    return null;
  }

  console.log('[ContentDetails] Rendering with content:', {
    title: content.title,
    year: content.year,
    rating: content.rating,
    duration: content.duration,
    description: content.description?.substring(0, 50) + '...',
    genre: content.genre,
    cast: content.cast
  });

  const isInWatchlist = currentProfile?.watchlist.includes(content.id);
  const relatedContent = content.genre?.[0] === 'Action' ? categories.action : categories.drama;

  return (
    <DetailsContainer>
      <HeroSection $backgroundImage={content.bannerUrl}>
        <CloseButton onClick={handleClose} aria-label="Close">
          ✕
        </CloseButton>

        <ContentWrapper
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Title>{content.title}</Title>

          <Metadata>
            {content.rating && content.rating !== 'Not Rated' && (
              <>
                <Rating>{content.rating}</Rating>
                <Separator>•</Separator>
              </>
            )}
            <span>{content.year}</span>
            <Separator>•</Separator>
            <span>{content.duration}</span>
            {content.seasons && (
              <>
                <Separator>•</Separator>
                <span>{content.seasons} Season{content.seasons !== 1 ? 's' : ''}</span>
              </>
            )}
          </Metadata>

          <Description>{content.description}</Description>

          <ButtonContainer>
            <PlayButton onClick={handlePlay} aria-label={`Play ${content.title}`}>
              <span>▶</span>
              Play
            </PlayButton>

            <MyListButton
              onClick={handleMyListToggle}
              aria-label={
                isInWatchlist
                  ? `Remove ${content.title} from My List`
                  : `Add ${content.title} to My List`
              }
            >
              <span>{isInWatchlist ? '✓' : '+'}</span>
              My List
            </MyListButton>
          </ButtonContainer>
        </ContentWrapper>
      </HeroSection>

      <DetailsSection>
        <DetailsGrid>
          <MainDetails>
            {content.trailerUrl && (
              <TrailerSection>
                <SectionTitle>Trailer</SectionTitle>
                <TrailerFrame
                  src={content.trailerUrl}
                  title={`${content.title} Trailer`}
                  allowFullScreen
                />
              </TrailerSection>
            )}

            {sources.length > 0 && (
              <SourcesSection>
                <SectionTitle>Available Sources ({sources.length})</SectionTitle>
                <SourcesList>
                  {sources.map((source, index) => (
                    <SourceButton
                      key={index}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      $source={source.source}
                    >
                      <span>▶</span>
                      {source.source}
                      <SourceBadgeText>{source.quality || 'HD'}</SourceBadgeText>
                    </SourceButton>
                  ))}
                </SourcesList>
              </SourcesSection>
            )}

            <SectionTitle>About {content.title}</SectionTitle>
            {content.cast && content.cast.length > 0 && (
              <CastList>
                <strong>Cast:</strong> {content.cast.join(', ')}
              </CastList>
            )}
          </MainDetails>

          <Sidebar>
            <SectionTitle>Genres</SectionTitle>
            <GenreList>
              {(content.genre || []).map((genre) => (
                <GenreTag key={genre}>{genre}</GenreTag>
              ))}
            </GenreList>
          </Sidebar>
        </DetailsGrid>

        <ContentRow
          title="More Like This"
          contentIds={relatedContent}
          rowIndex={0}
        />
      </DetailsSection>
    </DetailsContainer>
  );
};

export default ContentDetails;